import { getFormEnrichedData, getFormEnrichedDistributionCountries } from 'state/formData/formData.selectors';
import { getQuoteType } from 'state/quotes/selectors';
import { isQuoteTypeWithoutKid } from 'state/quotes/utils';
import { getFormQuoteId, getQuoteIsKidExpected, getQuoteRegulationInfos } from 'state/selectors/common';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

export function getQuoteIsKidRequired(state: AppState, workspaceType: QuoteWorkspaceType, tabId: string): boolean {
  const quoteId = getFormQuoteId(state, workspaceType, tabId);
  return getQuoteIsKidRequiredByQuoteId(state, tabId, quoteId);
}

export function getQuoteIsKidRequiredByQuoteId(state: AppState, tabId: string, quoteId: string | undefined): boolean {
  const quoteType = getQuoteType(state, quoteId);
  if (quoteType !== undefined && isQuoteTypeWithoutKid(quoteType)) {
    return false;
  }
  const isKidRequired = getFormEnrichedData(state, tabId).KidRequired as boolean;
  return isKidRequired;
}

export function getQuoteIsProductGovernanceRequired(state: AppState, tabId: string): boolean {
  const enrichedData = getFormEnrichedData(state, tabId);
  return enrichedData.ProductGovernanceRequired && enrichedData.Wrapper !== 'Swap';
}

export function isQuoteRegulationLoading(state: AppState, workspaceType: QuoteWorkspaceType, tabId: string): boolean {
  const regulationInfos = getQuoteRegulationInfos(state, workspaceType, tabId);
  const quoteId = getFormQuoteId(state, workspaceType, tabId);
  const isProductGovernanceRequired = getQuoteIsProductGovernanceRequired(state, tabId) as boolean;
  const isKidRequired = getQuoteIsKidRequired(state, workspaceType, tabId);
  const isKidExpected = getQuoteIsKidExpected(state, tabId, quoteId);
  const haveKidDocument = regulationInfos!.kidDocument?.some(x => !!x) as boolean; // kidDocument.any()
  const haveProductGovernanceDocumentation = regulationInfos!.productGovernanceDocumentation?.some(x => !!x) as boolean;
  const hasTheoreticalValue = regulationInfos.theoreticalValue?.some(x => !!x) ?? false;
  const isCostsAndChargesRequired = getQuoteIsCostsAndChargesRequired(state, tabId);
  const isTheoreticalValueRequired = getQuoteIsTheoreticalValueRequired(state, tabId);
  const isCostsAndChargesLoading = regulationInfos?.costAndChargeReceived !== true;

  return (
    (isProductGovernanceRequired && !haveProductGovernanceDocumentation) ||
    (isKidRequired && isKidExpected && !haveKidDocument) ||
    (isCostsAndChargesRequired && isCostsAndChargesLoading) ||
    (isTheoreticalValueRequired && !hasTheoreticalValue)
  );
}

export function isQuoteRegulationFailed(state: AppState, workspaceType: QuoteWorkspaceType, tabId: string): boolean {
  const regulationInfos = getQuoteRegulationInfos(state, workspaceType, tabId);
  const quoteId = getFormQuoteId(state, workspaceType, tabId);
  const isKidRequired = getQuoteIsKidRequired(state, workspaceType, tabId);
  const isKidExpected = getQuoteIsKidExpected(state, tabId, quoteId);
  const enrichedData = getFormEnrichedData(state, tabId);
  const haveKidDocumentError =
    (regulationInfos?.kidDocumentError?.some(x => !!x) as boolean) && isKidRequired && isKidExpected;
  const haveProductGovernanceDocumentationError = regulationInfos!.productGovernanceDocumentationError?.some(
    x => !!x,
  ) as boolean;
  const haveProductGovernanceAssessmentError =
    (regulationInfos?.productGovernanceAssessment?.rejections?.some(x => !!x) && enrichedData?.Wrapper !== 'Swap') ??
    false;

  return haveKidDocumentError || haveProductGovernanceDocumentationError || haveProductGovernanceAssessmentError;
}

export function hasAnyPreviousRegulationData(
  state: AppState,
  workspaceType: QuoteWorkspaceType,
  tabId: string,
): boolean {
  const regulationInfos = getQuoteRegulationInfos(state, workspaceType, tabId);

  const isProductGovernanceRequired = getFormEnrichedData(state, tabId).ProductGovernanceRequired as boolean;
  const haveProductGovernanceDocumentation = regulationInfos!.productGovernanceDocumentation?.some(x => !!x) as boolean;
  const haveProductAssessment = !!regulationInfos!.productGovernanceAssessment;

  const isKidRequired = getQuoteIsKidRequired(state, workspaceType, tabId);
  const haveKidDocument = regulationInfos!.kidDocument?.some(x => !!x) as boolean;

  const haveCostAndCharges = regulationInfos.costAndCharge?.some(x => !!x);

  return (
    haveCostAndCharges ||
    (isKidRequired && haveKidDocument) ||
    (isProductGovernanceRequired && (haveProductGovernanceDocumentation || haveProductAssessment))
  );
}

export function getQuoteIsCostsAndChargesRequired(state: AppState, tabId: string): boolean {
  const distributionCountries = getFormEnrichedDistributionCountries(state, tabId);
  return !(distributionCountries.includes('JPN') && distributionCountries.length === 1);
}

export function getQuoteIsTheoreticalValueRequired(state: AppState, tabId: string): boolean {
  const distributionCountries = getFormEnrichedDistributionCountries(state, tabId);
  return distributionCountries.includes('JPN');
}
